import React from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, SlidersHorizontal } from 'lucide-react';
import { equipmentCategories } from '../data/mock';
import {
  CATEGORY_OPTIONS,
  EQUIPMENT_TYPES_BY_CATEGORY,
  getAvailableRanges,
  getAvailablePounds,
} from '../lib/equipmentFilters';

const EquipmentFilterSidebar = ({ filters, handlers, sticky = true }) => {
  const {
    searchQuery,
    selectedCategory,
    selectedEquipmentType,
    selectedRange,
    selectedPounds,
    maxPrice,
    priceTimeframe,
  } = filters;

  const {
    setSearchQuery,
    setCategory,
    setEquipmentType,
    setRange,
    setPounds,
    setMaxPrice,
    setPriceTimeframe,
    clear,
  } = handlers;

  const availableRanges = getAvailableRanges(equipmentCategories, {
    selectedCategory,
    selectedEquipmentType,
  });
  const availablePounds = getAvailablePounds(equipmentCategories, {
    selectedCategory,
    selectedEquipmentType,
  });

  const showEquipmentTypeFilter = EQUIPMENT_TYPES_BY_CATEGORY[selectedCategory]?.length > 0;
  const showRangeFilter = availableRanges.length > 0;
  const showPoundsFilter = availablePounds.length > 0;

  return (
    <div
      className={`bg-white rounded-lg border-2 border-gray-200 p-6 ${sticky ? 'sticky top-24' : ''}`}
      data-testid="filters-sidebar"
    >
      <div className="flex items-center mb-6">
        <SlidersHorizontal className="w-5 h-5 text-[#E28724] mr-2" />
        <h3 className="text-xl font-bold text-gray-900">Filters</h3>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search equipment..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="filter-search-input"
          />
        </div>
      </div>

      {/* Category Type Filter */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Category Type</label>
        <div className="space-y-2">
          {CATEGORY_OPTIONS.map((category) => (
            <label key={category} className="flex items-center cursor-pointer group">
              <input
                type="radio"
                name="category"
                value={category}
                checked={selectedCategory === category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                data-testid={`filter-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">
                {category}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Equipment Type Filter */}
      {showEquipmentTypeFilter && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Equipment Type</label>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer group">
              <input
                type="radio"
                name="equipmentType"
                value="All"
                checked={selectedEquipmentType === 'All'}
                onChange={(e) => setEquipmentType(e.target.value)}
                className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                data-testid="filter-equipment-type-all"
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">All</span>
            </label>
            {EQUIPMENT_TYPES_BY_CATEGORY[selectedCategory].map((type) => (
              <label key={type} className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="equipmentType"
                  value={type}
                  checked={selectedEquipmentType === type}
                  onChange={(e) => setEquipmentType(e.target.value)}
                  className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                  data-testid={`filter-equipment-type-${type.toLowerCase().replace(/\s+/g, '-')}`}
                />
                <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">
                  {type}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Range Filter */}
      {showRangeFilter && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Range</label>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer group">
              <input
                type="radio"
                name="range"
                value="All"
                checked={selectedRange === 'All'}
                onChange={(e) => setRange(e.target.value)}
                className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                data-testid="filter-range-all"
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">All</span>
            </label>
            {availableRanges.map((bucket) => (
              <label key={bucket.label} className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="range"
                  value={bucket.label}
                  checked={selectedRange === bucket.label}
                  onChange={(e) => setRange(e.target.value)}
                  className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                  data-testid={`filter-range-${bucket.min}-${bucket.max}`}
                />
                <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">
                  {bucket.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Pounds Filter */}
      {showPoundsFilter && (
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-3">Lift Capacity</label>
          <div className="space-y-2">
            <label className="flex items-center cursor-pointer group">
              <input
                type="radio"
                name="pounds"
                value="All"
                checked={selectedPounds === 'All'}
                onChange={(e) => setPounds(e.target.value)}
                className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                data-testid="filter-pounds-all"
              />
              <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">All</span>
            </label>
            {availablePounds.map((opt) => (
              <label key={opt.label} className="flex items-center cursor-pointer group">
                <input
                  type="radio"
                  name="pounds"
                  value={opt.label}
                  checked={selectedPounds === opt.label}
                  onChange={(e) => setPounds(e.target.value)}
                  className="w-4 h-4 text-[#E28724] border-gray-300 focus:ring-[#E28724]"
                  data-testid={`filter-pounds-${opt.min}`}
                />
                <span className="ml-2 text-sm text-gray-700 group-hover:text-[#E28724]">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Price Filter */}
      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Price</label>
        <div className="space-y-3">
          <Input
            type="number"
            placeholder="Max price..."
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            className="w-full"
            data-testid="filter-max-price-input"
          />
          <select
            value={priceTimeframe}
            onChange={(e) => setPriceTimeframe(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E28724] focus:border-[#E28724]"
            data-testid="filter-price-timeframe-select"
          >
            <option value="daily">Per Day</option>
            <option value="weekly">Per Week</option>
            <option value="monthly">Per Month</option>
          </select>
        </div>
      </div>

      <Button
        onClick={clear}
        variant="outline"
        className="w-full border-[#E28724] text-[#E28724] hover:bg-[#E28724] hover:text-white"
        data-testid="clear-filters-button"
      >
        Clear All Filters
      </Button>
    </div>
  );
};

export default EquipmentFilterSidebar;
